// نتیجه (US-MED-050، 052، 053؛ FLOW-14): ثبت دستی ساختاریافته با پرچم خودکار سرور یا گزارش متنی و پیوست PDF (DOC)، و بازبینی با تفسیر و اقدام؛
// نتیجه بحرانی بدون «تماس ثبت شد» بازبینی نمی‌شود (PHX-MED-0207). مقادیر و تفسیر فقط برای کاربر بالینی (C-AGENT-871).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { unwrap, type MedOrderType, type MedOrderView, type MedResultView, type MedReviewAction } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, LoadingState, toJalali, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { FlagChip, medKeys, num } from './common';

const ACTIONS: readonly Exclude<MedReviewAction, null>[] = ['NoAction', 'ContactPatient', 'FollowUpAppointment', 'ChangeTreatment'];
const TYPES: readonly MedOrderType[] = ['Lab', 'Imaging', 'Other'];

interface ValueRow {
  analyteCode: string;
  value: string;
  unit: string;
  referenceLow: string;
  referenceHigh: string;
}

/** ثبت نتیجه با یا بدون درخواست (AC-MED-050.1 تا 050.3). */
export function EnterResultDialog({ open, patientId, order, onClose, onSaved }: { open: boolean; patientId: string; order?: MedOrderView | null; onClose: () => void; onSaved?: (result: MedResultView) => void }) {
  const { t } = useTranslation('med');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const [type, setType] = useState<MedOrderType>(order?.type ?? 'Lab');
  const [title, setTitle] = useState('');
  const [reportText, setReportText] = useState('');
  const [sampleDate, setSampleDate] = useState('');
  const [rows, setRows] = useState<ValueRow[]>(() => (order?.items ?? []).filter((item) => !item.resulted).map((item) => ({ analyteCode: item.code, value: '', unit: '', referenceLow: '', referenceHigh: '' })));
  const [file, setFile] = useState<File | null>(null);
  const set = (index: number, patch: Partial<ValueRow>) => setRows((current) => current.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  const decimal = (value: string) => (value.trim() === '' ? null : Number(value));
  const filled = rows.filter((row) => row.analyteCode.trim() && row.value.trim());

  const save = useMutation({
    mutationFn: async () => {
      const result = await unwrap(
        tenantApi.POST('/api/v1/medical-orders/results', {
          body: {
            patientId,
            orderId: order?.id ?? null,
            type: order ? null : type,
            title: title.trim() || null,
            reportText: reportText.trim() || null,
            sampleDate: sampleDate || null,
            values: filled.map((row) => ({
              analyteCode: row.analyteCode.trim().toUpperCase(),
              name: null,
              value: decimal(row.value),
              textValue: null,
              unit: row.unit.trim() || null,
              referenceLow: decimal(row.referenceLow),
              referenceHigh: decimal(row.referenceHigh),
              flag: null,
            })),
            responsibleProviderId: null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
      if (!file) {
        return result;
      }
      const form = new FormData();
      form.append('file', file);
      return unwrap(
        tenantApi.POST('/api/v1/medical-orders/results/{id}/report', {
          params: { path: { id: result.id } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => onSaved?.(result),
  });

  return (
    <FormDialog
      open={open}
      titleKey="med:result.enter_title"
      submitKey="med:result.save"
      maxWidth="md"
      onSubmit={() => (filled.length > 0 || reportText.trim() || file ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={filled.length > 0 || reportText.trim() !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      {order ? (
        <Typography variant="body2" data-testid="med-result-order">
          {t('med:result.for_order', { number: order.number })}
        </Typography>
      ) : (
        <TextField select label={t('med:order.type')} value={type} onChange={(event) => setType(event.target.value as MedOrderType)}>
          {TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:order_type.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Stack direction="row" spacing={1}>
        <TextField fullWidth label={t('med:result.title_field')} value={title} onChange={(event) => setTitle(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
        <TextField type="date" label={t('med:result.sample_date')} value={sampleDate} onChange={(event) => setSampleDate(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'med-result-sample-date' } }} />
      </Stack>
      <Table size="small" aria-label={t('med:result.values')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('med:result.analyte')}</TableCell>
            <TableCell>{t('med:result.value')}</TableCell>
            <TableCell>{t('med:result.unit')}</TableCell>
            <TableCell>{t('med:result.ref_low')}</TableCell>
            <TableCell>{t('med:result.ref_high')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index} data-testid="med-result-row">
              <TableCell>
                <TextField size="small" value={row.analyteCode} onChange={(event) => set(index, { analyteCode: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 32, 'aria-label': t('med:result.analyte'), 'data-testid': 'med-result-analyte' } }} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={row.value} onChange={(event) => set(index, { value: event.target.value })} slotProps={{ htmlInput: { 'aria-label': t('med:result.value'), 'data-testid': 'med-result-value' } }} />
              </TableCell>
              <TableCell>
                <TextField size="small" value={row.unit} placeholder={t('med:result.auto')} onChange={(event) => set(index, { unit: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', 'aria-label': t('med:result.unit') } }} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={row.referenceLow} placeholder={t('med:result.auto')} onChange={(event) => set(index, { referenceLow: event.target.value })} slotProps={{ htmlInput: { 'aria-label': t('med:result.ref_low') } }} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={row.referenceHigh} placeholder={t('med:result.auto')} onChange={(event) => set(index, { referenceHigh: event.target.value })} slotProps={{ htmlInput: { 'aria-label': t('med:result.ref_high') } }} />
              </TableCell>
              <TableCell>
                <IconButton size="small" aria-label={t('med:result.remove_value')} onClick={() => setRows((current) => current.filter((_, position) => position !== index))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button onClick={() => setRows((current) => [...current, { analyteCode: '', value: '', unit: '', referenceLow: '', referenceHigh: '' }])} data-testid="med-result-add-value">
        {t('med:result.add_value')}
      </Button>
      <TextField label={t('med:result.report_text')} value={reportText} multiline minRows={2} onChange={(event) => setReportText(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000 } }} />
      <Button component="label" variant="outlined">
        {file ? file.name : t('med:result.attach_pdf')}
        <input hidden type="file" accept="application/pdf,image/*" onChange={(event) => setFile(event.target.files?.[0] ?? null)} data-testid="med-result-file" />
      </Button>
    </FormDialog>
  );
}

/** جزئیات و بازبینی نتیجه (AC-MED-053.1؛ FLOW-14). */
export function ResultReviewDialog({ open, resultId, onClose, onReviewed }: { open: boolean; resultId: string; onClose: () => void; onReviewed?: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const canReview = access.accessMode === 'Full' && access.can('med.result.review');
  // AC-MED-054.1 (AP-CLN-04): انتشار برای مراجعه‌کننده با یادداشت عمومی (med.result.release).
  const canRelease = access.accessMode === 'Full' && access.can('med.result.release');
  const [releaseNow, setReleaseNow] = useState(false);
  const [publicNote, setPublicNote] = useState('');
  const scheduling = useSchedulingActions();
  const [interpretation, setInterpretation] = useState('');
  const [action, setAction] = useState<Exclude<MedReviewAction, null>>('NoAction');
  const [contactNote, setContactNote] = useState('');
  const [contacted, setContacted] = useState(false);
  const query = useQuery({ queryKey: medKeys.result(session?.tenantId, resultId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/results/{id}', { params: { path: { id: resultId } } })) });
  const result = query.data;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['medorders', session?.tenantId] });
  const contact = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/medical-orders/results/{id}:record-contact', { params: { path: { id: resultId } }, body: { note: contactNote.trim() || null } })),
    onSuccess: (next) => queryClient.setQueryData(medKeys.result(session?.tenantId, resultId), next),
  });
  const review = useMutation({
    mutationFn: async () => {
      if (result?.hasCritical && !result.contactRecordedAt && contacted) {
        await contact.mutateAsync();
      }
      return unwrap(
        tenantApi.POST('/api/v1/medical-orders/results/{id}:review', {
          params: { path: { id: resultId } },
          body: { interpretation: interpretation.trim() || null, action, release: canRelease && releaseNow, publicNote: canRelease && releaseNow ? publicNote.trim() || null : null },
        }),
      );
    },
    onSuccess: (reviewed) => {
      refresh();
      onReviewed?.();
      // AC-MED-053.1: اقدام «نوبت پیگیری» ← دیالوگ رزرو SCH پیش‌پر (C-AGENT-877).
      if (reviewed.action === 'FollowUpAppointment' && scheduling?.canBook) {
        scheduling.openBooking({ patient: { id: reviewed.patientId, displayName: '' } });
      }
    },
  });
  const release = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/medical-orders/results/{id}:release', { params: { path: { id: resultId } }, body: { publicNote: publicNote.trim() || null } })),
    onSuccess: (next) => {
      queryClient.setQueryData(medKeys.result(session?.tenantId, resultId), next);
      refresh();
    },
  });
  const needsContact = Boolean(result?.hasCritical && !result.contactRecordedAt && !contacted);

  return (
    <FormDialog
      open={open}
      titleKey="med:result.review_title"
      submitKey={canReview && result?.status === 'Received' ? 'med:result.reviewed' : 'med:common.close'}
      maxWidth="md"
      onSubmit={() => (canReview && result?.status === 'Received' ? (needsContact ? undefined : review.mutate()) : onClose())}
      onClose={onClose}
      isDirty={interpretation.trim() !== ''}
      isSubmitting={review.isPending}
      submitError={review.error}
    >
      {query.isPending ? (
        <LoadingState rows={4} />
      ) : query.error || !result ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <>
          {result.hasCritical && (
            <Alert severity="error" data-testid="med-result-critical">
              {t('med:result.critical')}
              {result.escalatedAt ? ` · ${t('med:result.escalated', { at: toJalaliDateTime(result.escalatedAt, { timeZone }) })}` : ''}
            </Alert>
          )}
          <Typography variant="body2">
            {result.title ?? t(`med:order_type.${result.type}`)} {result.orderNumber ? <bdi dir="ltr">· {result.orderNumber}</bdi> : null} · {t('med:result.received_at', { at: toJalaliDateTime(result.receivedAt, { timeZone }) })}
            {result.sampleDate ? ` · ${t('med:result.sample_on', { date: toJalali(result.sampleDate) })}` : ''}
          </Typography>
          {result.values.length > 0 && (
            <Table size="small" aria-label={t('med:result.values')} data-testid="med-result-values">
              <TableHead>
                <TableRow>
                  <TableCell>{t('med:result.analyte')}</TableCell>
                  <TableCell>{t('med:result.value')}</TableCell>
                  <TableCell>{t('med:result.reference')}</TableCell>
                  <TableCell>{t('med:result.flag')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.values.map((value) => (
                  <TableRow key={value.id}>
                    <TableCell>{value.name}</TableCell>
                    <TableCell>
                      <bdi dir="ltr">
                        {num(value.value) ?? value.textValue} {value.unit}
                      </bdi>
                    </TableCell>
                    <TableCell>
                      <bdi dir="ltr">{value.referenceLow !== null || value.referenceHigh !== null ? `${num(value.referenceLow) ?? ''}–${num(value.referenceHigh) ?? ''}` : '—'}</bdi>
                    </TableCell>
                    <TableCell>
                      <FlagChip flag={value.flag} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {result.reportText && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{result.reportText}</Typography>}
          {result.documentId && <Typography variant="body2" data-testid="med-result-document">{t('med:result.has_document')}</Typography>}
          {/* AC-MED-056.1 (AP-INT-05): نمایشگر DICOM برای نتیجه تصویربرداری — از PACS متصل یا فایل پیوست (SCR-STAFF-704). */}
          {result.type === 'Imaging' && (
            <Button
              component={RouterLink}
              to={`/desk/results/${result.id}/imaging`}
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
              data-testid="med-result-imaging"
            >
              {t('med:imaging.open')}
            </Button>
          )}
          {result.status === 'Reviewed' || result.status === 'Released' ? (
            <>
              <Alert severity="success" data-testid="med-result-reviewed">
                {t('med:result.reviewed_by', { name: result.reviewedByName ?? '', action: result.action ? t(`med:review_action.${result.action}`) : '' })}
                {result.interpretation ? ` · ${result.interpretation}` : ''}
              </Alert>
              {result.status === 'Released' ? (
                <Alert severity="info" data-testid="med-result-released">
                  {t('med:result.released_at', { at: result.releasedAt ? toJalaliDateTime(result.releasedAt, { timeZone }) : '' })}
                  {result.publicNote ? ` · ${result.publicNote}` : ''}
                </Alert>
              ) : canRelease ? (
                <Stack spacing={1} data-testid="med-result-release-panel">
                  <TextField label={t('med:result.public_note')} helperText={t('med:result.public_note_hint')} value={publicNote} multiline minRows={2} onChange={(event) => setPublicNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'med-result-public-note' } }} />
                  {release.error ? <ErrorState error={release.error} compact /> : null}
                  <Button variant="contained" onClick={() => release.mutate()} disabled={release.isPending} data-testid="med-result-release">
                    {t('med:result.release')}
                  </Button>
                </Stack>
              ) : null}
            </>
          ) : canReview ? (
            <>
              <TextField label={t('med:result.interpretation')} value={interpretation} multiline minRows={2} onChange={(event) => setInterpretation(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'med-result-interpretation' } }} />
              <RadioGroup row value={action} onChange={(event) => setAction(event.target.value as Exclude<MedReviewAction, null>)} aria-label={t('med:result.action')}>
                {ACTIONS.map((value) => (
                  <FormControlLabel key={value} value={value} control={<Radio size="small" />} label={t(`med:review_action.${value}`)} data-testid={`med-review-action-${value}`} />
                ))}
              </RadioGroup>
              {result.hasCritical && !result.contactRecordedAt && (
                <Stack spacing={1}>
                  <FormControlLabel control={<Checkbox checked={contacted} onChange={(event) => setContacted(event.target.checked)} />} label={t('med:result.contact_recorded')} data-testid="med-result-contacted" />
                  {contacted && <TextField size="small" label={t('med:result.contact_note')} value={contactNote} onChange={(event) => setContactNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} />}
                  {needsContact && <Typography variant="body2" color="error" data-testid="med-result-contact-required">{t('med:result.contact_required')}</Typography>}
                </Stack>
              )}
              {result.hasCritical && result.contactRecordedAt && <Typography variant="body2" data-testid="med-result-contact-done">{t('med:result.contact_done', { at: toJalaliDateTime(result.contactRecordedAt, { timeZone }) })}</Typography>}
              {canRelease && (
                <Stack spacing={1}>
                  <FormControlLabel control={<Checkbox checked={releaseNow} onChange={(event) => setReleaseNow(event.target.checked)} />} label={t('med:result.review_and_release')} data-testid="med-result-release-now" />
                  {releaseNow && <TextField label={t('med:result.public_note')} helperText={t('med:result.public_note_hint')} value={publicNote} multiline minRows={2} onChange={(event) => setPublicNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'med-result-review-public-note' } }} />}
                </Stack>
              )}
            </>
          ) : null}
        </>
      )}
    </FormDialog>
  );
}
